import { Flex } from '@chakra-ui/react';
import { useDraggable } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { MouseEventHandler, memo, useCallback } from 'react';
import { match } from 'ts-pattern';
import { EditIcon, IconButton, MoveDotsIcon, TrashIcon } from 'ui-components';

import { usePageBuilderStore } from '../hooks/usePageBuilderStore';
import { useBrandExperienceModalStore } from '../modals/BrandExperienceModal/useBrandExperienceModalStore';
import { useDeleteBlockStore } from '../modals/DeleteConfirmation/store';
import { usePlatformAnalyticsStore } from '../modals/PlatformAnalytics';
import { useDropActions } from '../sidebar/useDropActions';
import { AnyBlock, BlockType } from '../types';

interface BlockControlsProps {
  block: AnyBlock;
  component: BlockType;
  dragHandleProps?: SyntheticListenerMap;
}

export const BlockControls = memo<BlockControlsProps>(
  ({ block, component, dragHandleProps }) => {
    const { setPlatform: setSelectedPlatform } = usePlatformAnalyticsStore();
    const blockId = block.id;
    const triggerActions = useDropActions();
    const {
      editingBlockId,
      setEditingBlockId,
      blocks,
      updateBlock,
      removeBlock,
    } = usePageBuilderStore();
    const { openModalToDeleteBlock } = useDeleteBlockStore();
    const { setIsOpen: setBrandExperienceModalOpen } =
      useBrandExperienceModalStore();

    const handleRemove = useCallback(() => {
      openModalToDeleteBlock(block);
    }, [block, openModalToDeleteBlock]);

    const handleEditChart = useCallback(() => {
      if (block.component === 'TalentCharts') {
        const platform = block.properties.platform;
        setSelectedPlatform(platform);
        triggerActions('TalentCharts', { platform });
      }
    }, [block, setSelectedPlatform, triggerActions]);

    const handleEditText: MouseEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        e.stopPropagation();
        if (block.component === 'EditableRichTextView') {
          setEditingBlockId(blockId);
        }
      },
      [blockId, block.component, setEditingBlockId],
    );

    const handleEditVideo: MouseEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        e.stopPropagation();
        if (block.component === 'TalentVideo') {
          triggerActions('TalentVideo', {
            isEdit: true,
            blockId,
            url: block.properties.url,
          });
        }
      },
      [blockId, block.component, triggerActions, block.properties],
    );

    const handleOpenAddContent = useCallback(() => {
      triggerActions('TalentContent', {
        isEdit: true,
        blockId,
      });
    }, [triggerActions, blockId]);

    const handleEditBrandExperience = useCallback(() => {
      // Get the current block to access existing logos
      const currentBlock = blocks.find((b) => b.id === blockId);
      if (!currentBlock) return;

      setBrandExperienceModalOpen(true, {
        callback: (data: { logos: any[] }) => {
          // If no brand items, remove the block completely
          if (data.logos.length === 0) {
            removeBlock(blockId);
            return;
          }

          // Update the block with new logos
          updateBlock(blockId, {
            ...currentBlock,
            properties: {
              ...currentBlock.properties,
              logos: data.logos,
            },
          } as any);
        },
      });
    }, [
      setBrandExperienceModalOpen,
      blocks,
      blockId,
      updateBlock,
      removeBlock,
    ]);

    const isEditing = editingBlockId === blockId;

    // Early return if EditableRichTextView is in edit mode
    if (component === 'EditableRichTextView' && isEditing) {
      return null;
    }

    return (
      <Flex
        position="absolute"
        left="50%"
        top={0}
        alignItems="center"
        opacity="0"
        _groupHover={{ opacity: '1' }}
        transform="translate(-50%, -50%)"
        transition="ease-in-out 300ms"
        bg="white"
        borderRadius="100px"
        p={1}
        boxShadow="blockOverlay"
        gap={2}
        zIndex={1}
        data-testid={`block-controls-${blockId}`}
      >
        {match(component)
          .with('TalentCharts', () => (
            <>
              <MoveButton dragHandleProps={dragHandleProps} />
              <IconButton
                onClick={handleEditChart}
                aria-label="Edit block"
                icon={<EditIcon boxSize={4} color="black" />}
                trackId="media-kit-block-edit-chart-btn"
                variant="ghost"
                size="sm"
                _hover={{ bg: 'gray.100' }}
              />
              <DeleteButton onDelete={handleRemove} />
            </>
          ))
          .with('TalentProfile', () => (
            <MoveButton dragHandleProps={dragHandleProps} />
          ))
          .with('TalentContent', () => (
            <>
              <MoveButton dragHandleProps={dragHandleProps} />
              <IconButton
                onClick={handleOpenAddContent}
                aria-label="Open Add Content"
                icon={<EditIcon w={4} color="black" />}
                variant="ghost"
                size="sm"
                _hover={{ bg: 'gray.100' }}
                data-testid="edit-content-button"
                trackId="media-kit-block-edit-content-btn"
              />
              <DeleteButton onDelete={handleRemove} />
            </>
          ))
          .with('EditableRichTextView', () => (
            <>
              <MoveButton dragHandleProps={dragHandleProps} />
              <IconButton
                onClick={handleEditText}
                aria-label="Edit text block"
                icon={<EditIcon color="black" />}
                trackId="media-kit-block-edit-text-btn"
                variant="ghost"
                size="sm"
                _hover={{ bg: 'gray.100' }}
              />
              <DeleteButton onDelete={handleRemove} />
            </>
          ))
          .with('TalentVideo', () => (
            <>
              <MoveButton dragHandleProps={dragHandleProps} />
              <IconButton
                onClick={handleEditVideo}
                aria-label="Edit video block"
                icon={<EditIcon color="black" />}
                trackId="media-kit-block-edit-video-btn"
                variant="ghost"
                size="sm"
                _hover={{ bg: 'gray.100' }}
              />
              <DeleteButton onDelete={handleRemove} />
            </>
          ))
          .with('BrandLogos', () => (
            <>
              <MoveButton dragHandleProps={dragHandleProps} />
              <IconButton
                onClick={handleEditBrandExperience}
                aria-label="Edit brand experience block"
                icon={<EditIcon w={4} color="black" />}
                variant="ghost"
                size="sm"
                _hover={{ bg: 'gray.100' }}
                data-testid="edit-brand-experience-button"
                trackId="media-kit-block-edit-brand-experience-btn"
              />
              <DeleteButton onDelete={handleRemove} />
            </>
          ))
          .otherwise(() => null)}
      </Flex>
    );
  },
);

const MoveButton = memo<{ dragHandleProps?: SyntheticListenerMap }>(
  ({ dragHandleProps }) => {
    const { setActivatorNodeRef } = useDraggable({
      id: 'draggable',
    });

    return (
      <IconButton
        ref={setActivatorNodeRef}
        aria-label="Move block"
        icon={<MoveDotsIcon w={4} color="black" />}
        variant="ghost"
        size="sm"
        {...dragHandleProps}
        trackId="media-kit-block-reorder-btn"
        _hover={{ bg: 'gray.100' }}
        _active={{ cursor: 'grabbing' }}
        sx={{ cursor: 'grab' }}
        data-testid="move-button"
      />
    );
  },
);

const DeleteButton = memo<{ onDelete: () => void }>(({ onDelete }) => (
  <IconButton
    onClick={onDelete}
    aria-label="Delete block"
    icon={<TrashIcon w={4} color="black" />}
    variant="ghost"
    trackId="media-kit-block-delete-btn"
    size="sm"
    _hover={{ bg: 'gray.100' }}
    data-testid="delete-button"
  />
));
